import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getBook } from '../services/api';
import './BookDetail.css';

interface Chapter {
  id: number;
  book_id: number;
  title: string;
  order_num: number;
}

interface Book {
  id: number;
  title: string;
  description: string;
  cover_image: string;
  author: string;
  year: number;
  genre: string;
  chapters: Chapter[];
}

const BookDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [book, setBook] = useState<Book | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      getBook(parseInt(id))
        .then((res) => setBook(res.data))
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [id]);

  if (loading) {
    return (
      <div className="loading-container" style={{ paddingTop: 120 }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!book) {
    return (
      <div className="loading-container" style={{ paddingTop: 120 }}>
        <p>Book not found</p>
      </div>
    );
  }

  return (
    <div className="book-detail-page">
      <section className="book-detail-hero">
        <div className="container">
          <motion.div
            className="book-detail-grid"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="book-detail-cover">
              <div className="detail-book-visual">
                <div className="detail-spine" />
                <div className="detail-front">
                  <span className="detail-infinity">&#8734;</span>
                  <h3>{book.title}</h3>
                  <div className="detail-line" />
                  <p>{book.author}</p>
                </div>
              </div>
            </div>

            <div className="book-detail-info">
              <span className="page-badge">{book.genre}</span>
              <h1 className="book-detail-title">{book.title}</h1>
              <p className="book-detail-author">by {book.author}</p>
              <p className="book-detail-desc">{book.description}</p>

              <div className="book-detail-meta">
                <div className="detail-meta-item">
                  <span className="meta-label">Published</span>
                  <span className="meta-value">{book.year}</span>
                </div>
                <div className="detail-meta-item">
                  <span className="meta-label">Chapters</span>
                  <span className="meta-value">{book.chapters?.length || 0}</span>
                </div>
                <div className="detail-meta-item">
                  <span className="meta-label">Genre</span>
                  <span className="meta-value">{book.genre}</span>
                </div>
              </div>

              {book.chapters && book.chapters.length > 0 && (
                <Link
                  to={`/read/book/${book.id}/chapter/${book.chapters[0].id}`}
                  className="btn btn-primary"
                  style={{ marginTop: 24 }}
                >
                  Start Reading
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="chapters-section section">
        <div className="container">
          <h2 className="section-title">Chapters</h2>
          <p className="section-subtitle">Select a chapter to begin reading</p>

          <div className="chapters-list">
            {book.chapters?.map((chapter, index) => (
              <motion.div
                key={chapter.id}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/read/book/${book.id}/chapter/${chapter.id}`}
                  className="chapter-item"
                >
                  <div className="chapter-number">
                    <span>{String(chapter.order_num).padStart(2, '0')}</span>
                  </div>
                  <div className="chapter-info">
                    <h3>{chapter.title}</h3>
                    <p>Tap to read</p>
                  </div>
                  <span className="chapter-arrow">&rarr;</span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookDetail;
